import JsBarcode from "jsbarcode";
import moment from "moment";
import { getCompanyReportInfo } from "@dhn/shared/company-info";
import { formatPhone, toTitleCase } from "@dhn/shared/utils";

export function appointmentHistoryReports(
  {
    dataRecipe = null,
    medicalStaff = null,
    patient = null,
    ars = null,
    headerCurrent,
    dataLicenses,
    dataQuotations,
    optionReport,
    companyInfo,
    documentCode: documentCodeProp,
  },
  { products, table },
  createAt
) {
  console.log(createAt);
  const companyReportInfo = getCompanyReportInfo(companyInfo);
  const doctorName = toTitleCase(
    `${medicalStaff?.firstName || ""} ${medicalStaff?.lastName || ""}`
  );
  const patientName = toTitleCase(
    `${patient?.firstName || ""} ${patient?.lastName || ""}`
  );
  const doctorPhones = [
    formatPhone(medicalStaff?.phone),
    medicalStaff?.phone2 ? `Tel. 2: ${formatPhone(medicalStaff.phone2)}` : "",
    medicalStaff?.cellphone ? `Cel: ${formatPhone(medicalStaff.cellphone)}` : "",
  ]
    .filter(Boolean)
    .join(" / ");

  // =========================
  // Utilidades
  // =========================
  const generateBarCode = (text) => {
    if (!text) return null;
    const canvas = document.createElement("canvas");
    // El número se imprime aparte como texto, por eso no se repite bajo las barras.
    JsBarcode(canvas, text, { format: "CODE128", displayValue: false });
    return canvas.toDataURL("image/png");
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return "";
    const birthDate = moment(dateOfBirth, "YYYY-MM-DD");
    return moment().diff(birthDate, "years");
  };

  // Máscara de cédula RD: 3-7-1
  const maskCedula = (raw) => {
    if (!raw) return "";
    const d = String(raw).replace(/\D/g, "").slice(0, 11);
    if (d.length !== 11) return raw;
    return `${d.slice(0, 3)}-${d.slice(3, 10)}-${d.slice(10)}`;
  };

  // =========================
  // Contenido principal (tabla/listado)
  // =========================
  const showDataOption2 = ["Referimiento Médico", "Licencia Medica"].includes(
    headerCurrent
  )
    ? { columns: [products[0]] }
    : {
        table: {
          headerRows: 1,
          ...(optionReport !== 4 && optionReport !== 5
            ? { widths: Array(products[0]?.length || 5).fill("*") }
            : {}),
          body: [table, ...products],
        },
      };

  // Header de texto reutilizable
  const headerCurrentStoredBase = {
    text: headerCurrent || "",
    fontSize: 16,
    bold: true,
    decoration: "underline",
    alignment: "right",
  };

  // Número del documento: imágenes, laboratorios y referimientos lo reciben del
  // llamador; licencia, receta y cotización lo toman de sus datos.
  const documentCode =
    documentCodeProp ??
    (optionReport === 2
      ? dataLicenses?.code
      : optionReport === 4
      ? dataRecipe?.prescriptionNumber
      : optionReport === 5
      ? dataQuotations?.code
      : "");
  const documentCodeLabel = documentCode
    ? `${optionReport === 4 ? "Receta No.:" : "No."} ${documentCode}`
    : "";
  const barcodeImage = generateBarCode(documentCode || "");

  // Documentos con fechas de emisión y vencimiento.
  const hasDocDates = [2, 4, 5].includes(optionReport);

  // ¿Este reporte usa el área superior de barcode/header/fechas?
  const isReportWithBarcodeArea = hasDocDates || !!barcodeImage;

  // Empuje para header cuando NO hay barcode (para que quede en la misma Y)
  const HEADER_PUSH_NO_BARCODE = 78; // ajusta si tu barcode real es más/menos alto

  let issueStr = "";
  let expiryStr = "";

  switch (optionReport) {
    case 2: // Licencia Médica
      issueStr = moment(dataLicenses.date).format("DD/MM/YYYY");

      expiryStr = moment(
        moment(dataLicenses.date).add(dataLicenses.days, "days")
      ).format("DD/MM/YYYY");

      break;
    case 4: // Receta Médica
      issueStr = moment(dataRecipe.date).format("DD/MM/YYYY");

      expiryStr = moment(dataRecipe.expiryDate).format("DD/MM/YYYY");

      break;
    case 5:
      issueStr = moment(dataQuotations.date).format("DD/MM/YYYY");

      expiryStr = moment(dataQuotations.expirationDate).format("DD/MM/YYYY");

      break;
    default:
  }

  let topDocDatesBlock = null;
  let topCreateAtBlock = null;

  if (hasDocDates) {
    topDocDatesBlock = {
      margin: [0, 2, 0, 0],
      alignment: "right",
      stack: [
        {
          text: [{ text: "Fecha Emisión: ", bold: true }, { text: issueStr }],
        },
        {
          text: [
            {
              text:
                optionReport === 2
                  ? "Fecha de Caducidad: "
                  : optionReport === 4
                  ? "Fecha Vencimiento: "
                  : "Valida Hasta:  ",
              bold: true,
            },
            { text: expiryStr },
          ],
        },
      ],
    };
  }

  if (isReportWithBarcodeArea && !hasDocDates) {
    topCreateAtBlock = {
      margin: [0, 2, 0, 0],
      alignment: "right",
      stack: [
        {
          text: [
            { text: "Fecha Emisión: ", bold: true },
            { text: moment(createAt).format("DD/MM/YYYY") },
          ],
        },
      ],
    };
  }

  // =========================
  // Pila derecha (barcode -> header -> fechas)
  //  * HAS BARCODE:    [barcode] -> [header] -> [topDocDatesBlock]
  //  * NO BARCODE:     [header con margin-top] -> [topCreateAtBlock si existe]
  // =========================
  const rightTopStack = [];
  if (isReportWithBarcodeArea) {
    if (barcodeImage) {
      rightTopStack.push({
        image: barcodeImage,
        width: 150,
        height: 60,
        margin: [0, 0, 0, 0],
      });
    }
    if (documentCodeLabel) {
      rightTopStack.push({
        text: documentCodeLabel,
        bold: true,
        alignment: "right",
        margin: [0, 2, 0, 0],
      });
    }
    rightTopStack.push({ ...headerCurrentStoredBase, margin: [0, 6, 0, 4] });
    if (topDocDatesBlock) rightTopStack.push(topDocDatesBlock);
    if (topCreateAtBlock) rightTopStack.push(topCreateAtBlock);
  } else {
    // NO hay barcode: header fijo en su Y gracias al margin, y LUEGO createAt (si viene).
    rightTopStack.push({
      ...headerCurrentStoredBase,
      margin: [0, HEADER_PUSH_NO_BARCODE, 20, 4],
    });
    if (topCreateAtBlock) rightTopStack.push(topCreateAtBlock);
  }

  // =========================
  // Fila superior: IZQ (logo + médico) | DER (stack)
  // =========================
  const topRow = {
    columns: [
      [
        { image: companyReportInfo.logoDataUrl, width: 150, margin: [0, 0, 0, 0] },
        {
          margin: [0, 4, 0, 10],
          stack: [
            {
              text: `${medicalStaff?.genderId === 1 ? "Dra." : "Dr. "}${doctorName}`,
              alignment: "left",
              bold: true,
              fontSize: 16,
            },
            {
              text:
                medicalStaff?.specialitiesByMedical?.[0]?.speciality?.name ||
                "",
              alignment: "left",
              margin: [0, 2, 0, 0],
            },
          ],
        },
      ],
      [
        isReportWithBarcodeArea
          ? { alignment: "right", fontSize: 10, stack: rightTopStack }
          : {
              alignment: "right",
              stack: [
                { ...headerCurrentStoredBase, margin: [0, 86, 0, 4] },
                {
                  margin: [0, 2, 0, 0],
                  alignment: "right",
                  stack: [
                    {
                      text: [
                        { text: "Fecha Emisión: ", bold: true },
                        { text: moment(createAt).format("DD/MM/YYYY") },
                      ],
                    },
                  ],
                },
              ],
            },
      ],
    ],
  };

  // =========================
  // Documento final
  // =========================
  return {
    pageMargins: [30, 30, 30, 20],
    content: [
      // Superior
      topRow,

      // Exequatur / Teléfono
      {
        columns: [
          [
            {
              columns: [
                {
                  text: "Exequatur:",
                  margin: [0, 0, 0, 0],
                  bold: true,
                  width: "auto",
                },
                {
                  text: ` ${medicalStaff?.exequatur || ""}`,
                  margin: [0, 0, 0, 10],
                  decoration: "underline",
                  width: "auto",
                },
              ],
            },
          ],
          [
            {
              columns: [
                {
                  text: "Tel:",
                  margin: [0, 0, 0, 0],
                  bold: true,
                  width: "auto",
                },
                {
                  text: ` ${doctorPhones}`,
                  margin: [0, 0, 0, 10],
                  decoration: "underline",
                  width: "auto",
                },
              ],
            },
          ],
        ],
      },

      // Separador
      {
        canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 }],
        margin: [0, 5, 0, 5],
      },

      // Paciente
      {
        text: `Paciente: ${patientName}`,
        bold: true,
        fontSize: 12,
        margin: [0, 5, 0, 5],
      },

      // Edad / Género / Cédula
      {
        columns: [
          [
            {
              columns: [
                {
                  text: "Edad:",
                  margin: [0, 0, 0, 0],
                  bold: true,
                  width: "auto",
                },
                {
                  text: ` ${
                    patient?.dateOfBirth
                      ? `${calculateAge(patient?.dateOfBirth)} años`
                      : ""
                  }`,
                  margin: [0, 0, 0, 5],
                  decoration: "underline",
                  width: "auto",
                },
              ],
            },
          ],
          [
            {
              columns: [
                {
                  text: "Género:",
                  margin: [0, 0, 0, 0],
                  bold: true,
                  width: "auto",
                },
                {
                  text: ` ${patient?.gender?.name || ""}`,
                  margin: [0, 0, 0, 5],
                  decoration: "underline",
                  width: "auto",
                },
              ],
            },
          ],
          [
            {
              columns: [
                {
                  text: "Cédula:",
                  margin: [0, 0, 0, 0],
                  bold: true,
                  width: "auto",
                },
                {
                  text: ` ${maskCedula(patient?.identificationCard)}`,
                  margin: [0, 0, 0, 5],
                  decoration: "underline",
                  width: "auto",
                },
              ],
            },
          ],
        ],
      },

      // Aseguradora
      {
        columns: [
          [
            {
              columns: [
                {
                  text: "Aseguradora:",
                  margin: [0, 0, 0, 0],
                  bold: true,
                  width: "auto",
                },
                {
                  text: ` ${ars?.name || ""}`,
                  margin: [0, 0, 0, 10],
                  decoration: "underline",
                  width: "auto",
                },
              ],
            },
          ],
          [{}],
          [{}],
        ],
      },

      // Tabla / productos
      { margin: [0, 0, 0, 50], ...showDataOption2 },

      // Nota
      ...(optionReport === 5
        ? [{
        text: "Esta cotización no es válida si ha excedido el período de vigencia y/o si no está debidamente sellada y firmada.",
        fontSize: 10,
        alignment: "left",
        margin: [0, 0, 24, 40],
          }]
        : []),

      // Firma final
      {
        text: "__________________________________",
        style: { alignment: "center" },
        margin: [0, 0, 0, 5],
      },
      {
        text: `${medicalStaff?.genderId === 1 ? "Dra." : "Dr. "}${doctorName}`,
        style: { alignment: "center" },
        margin: [0, 0, 0, 6],
      },
      {
        text: "Firma y sello del médico",
        style: { alignment: "center" },
        margin: [0, 0, 0, 24],
      },
    ],
  };
}
